import { ArrayOrSingle } from "src/modules/sm-base/shared/array-or-single.model";
import { OrdinaryObject, OrdinaryObjectNumber } from "./ordinary-object.model";
import { Utils } from "./utils";
import { Objects } from "./objects";
import seedrandom from 'seedrandom';

export class ArraysInternal {

    addThrough<T>(arr: T[], toAdd: T): T {
        arr.push(toAdd);
        return toAdd;
    }

    as<T>(value: ArrayOrSingle<T>): T[] {
        return this.is(value) ? value as T[] : [value as T];
    }

    copy<T>(arr: T[]): T[] {
        return arr == null ? null : [...arr];
    }

    crop<T>(arr: T[], maxLength: number, ellipses = false): T[] {
        if (arr == null) {
            return null;
        }
        if (arr.length > maxLength) {
            return ellipses ? [...arr.slice(0, maxLength - 1), "..." as any] : arr.slice(0, maxLength);
        }
        return arr;
    }

    createArray2Dim<T>(dim1: number, dim2: number, def: T): T[][] {
        let result: T[][] = [];
        for (let i = 0; i < dim1; i++) {
            result.push(Utils.getArrayOfConstant(def, dim2));
        }
        return result;
    }

    createArray3Dim<T>(dim1: number, dim2: number, dim3: number, def: T): T[][][] {
        let result: T[][][] = Utils.getArrayOfConstant(null as T[][], dim1);
        for (let i = 0; i < dim1; i++) {
            result[i] = Utils.getArrayOfConstant(null as T[], dim2);
            for (let j = 0; j < dim2; j++) {
                result[i][j] = Utils.getArrayOfConstant(def, dim3);
            }
        }
        return result;
    }

    ensure<T>(arr: T[]): T[] {
        return arr || [];
    }

    everyAndSome<T>(arr: T[], predicate: (item: T) => boolean): boolean {
        return arr.length > 0 && arr.every(predicate);
    }

    equalsFlat<T>(arr1: T[], arr2: T[]): boolean {
        if (arr1 == null && arr2 == null) {
            return true;
        }
        if (arr1 == null || arr2 == null) {
            return false;
        }
        if (arr1.length != arr2.length) {
            return false;
        }
        for (const [i, element] of arr1.entries()) {
            if (element != arr2[i]) {
                return false;
            }
        }
        return true;
    }

    equalsFlatUnordered<T>(arr1: T[], arr2: T[]): boolean {
        if (arr1 == null && arr2 == null) {
            return true;
        }
        if (arr1 == null || arr2 == null) {
            return false;
        }
        if (arr1.length != arr2.length) {
            return false;
        }
        const counts = new Map<T, number>();

        for (const item of arr1) {
            counts.set(item, (counts.get(item) ?? 0) + 1);
        }

        for (const item of arr2) {
            const count = counts.get(item);
            if (!count) {
                return false;
            }
            if (count === 1) {
                counts.delete(item);
            } else {
                counts.set(item, count - 1);
            }
        }

        return counts.size === 0;
    }

    explode<T, R>(arr: T[], exploder: (item: T) => R[]): R[] {
        let result: R[] = [];
        for (let item of arr) {
            result = [...result, ...exploder(item) ?? []];
        }
        return result;
    }


    findAndConvert<T, R>(arr: T[], predicate: (item: T) => boolean, converter: (item: T) => R, def: R = null): R {
        let result = arr.find(predicate);
        return result != null ? converter(result) : def;
    }

    fromIt<T>(it: IterableIterator<T>): T[] {
        let result: T[] = [];
        for (let item of it) {
            result.push(item);
        }
        return result;
    }

    getChanges<T>(arr1: T[], arr2: T[]): { added: T[]; deleted: T[]; eq: boolean } {
        let result: { added: T[]; deleted: T[]; eq: boolean } = { added: [], deleted: [], eq: true};

        for (let item of arr1) {
            if (!arr2.includes(item)) {
                result.deleted.push(item);
            }
        }
        for (let item of arr2) {
            if (!arr1.includes(item)) {
                result.added.push(item);
            }
        }

        result.eq = result.added.length == 0 && result.deleted.length == 0;
        return result;
    }

    getOnlyUpdatedEntries<T>(newEntries: T[], oldEntries: T[], matchFunction: (newItem: T, oldItem: T) => boolean, updatePred: (newItem: T, oldItem: T) => boolean): T[] {
        let result: T[] = [];
        for (let newItem of newEntries) {
            let oldItem = oldEntries.find(item => matchFunction(item, newItem));
            if (oldItem == null || updatePred(newItem, oldItem)) {
                result.push(newItem);
            }
        }
        return result;
    }

    getOrAdd<T>(arr: T[], matcher: (item: T) => boolean, generator: () => T): T {
        let result = arr.find(matcher);
        if (result == null) {
            result = generator();
            arr.push(result);
        }
        return result;
    }

    getRandom<T>(arr: T[], count: number): T[] {
        let len = arr.length;
        if (count > len) {
            count = len;
        }
        let result: any[] = new Array(count);
        let taken: any[] = new Array(len);
        while (count--) {
            let x = Math.floor(Math.random() * len);
            result[count] = arr[x in taken ? taken[x] : x];
            taken[x] = --len in taken ? taken[len] : len;
        }
        return result;
    }

    getSafe<T>(arr: ArrayLike<T>, index: number, defValue: T = null): T {
        return arr != null && index < arr.length ? arr[index] : defValue;
    }

    getSubListsFromBeginning<T>(arr: T[]): T[][] {
        let result: T[][] = [];
        for (let i = 0; i < arr.length; i++) {
            result.push(arr.slice(0, i + 1));
        }
        return result;
    }

    getUnique<T>(arr: T[], toKeyConverter: (item: T) => any = null): T[] {
        if (arr == null) {
            return null;
        }
        if (toKeyConverter != null) {
            let result = [];
            let keys = new Set();
            for (let item of arr) {
                let key = toKeyConverter(item);
                if (!keys.has(key)) {
                    keys.add(key);
                    result.push(item);
                }
            }
            return result;
        }
        else {
            return [...new Set(arr)];
        }
    }

    includesAny<T>(arr: T[], checkIfIncluded: T[]): boolean {
        for (let c of checkIfIncluded) {
            if (arr.includes(c)) {
                return true;
            }
        }
        return false;
    }

    is(object: any): boolean {
        return Array.isArray(object);
    }

    isEmpty(arr: any[]): boolean {
        return arr == null || arr.length == 0;
    }

    itemsToString<T>(arr: T[], sep = ", ", converter: (item: T) => string = null, lastSep?: string): string {
        let result = "";
        if (this.isEmpty(arr)) {
            return result;
        }
        let first = true;
        let i = 0;
        for (let item of arr) {
            result += (first ? "" : lastSep != null && i == arr.length - 1 ? lastSep : sep) + (converter != null ? converter(item) : Objects.toString(item));
            first = false;
            i++;
        }
        return result;
    }

    mapTwo<T1, T2, R>(list1: T1[], list2: T2[], converter: (item1: T1, item2: T2) => R): R[] {
        let result: R[] = [];
        for (let i = 0; i < Math.max(list1.length, list2.length); i++) {
            result.push(converter(Arrays.getSafe(list1, i), Arrays.getSafe(list2, i)));
        }
        return result;
    }

    mapWithIndex<T, R>(arr: T[], converter: (item: T, index: number) => R): R[] {
        let result: R[] = [];
        for (const [i, element] of arr.entries()) {
            result.push(converter(element, i));
        }
        return result;
    }

    max<T>(arr: T[], converter?: (item: T) => any): T | undefined {
        if (Utils.isNoe(arr)) {
            return null;
        }
        return arr.reduce((max, current) => Objects.cmp(converter != null ? converter(current) : current, converter != null ? converter(max) : max) > 0 ? current : max);
    }

    min<T>(arr: T[], converter?: (item: T) => any): T | undefined {
        if (Utils.isNoe(arr)) {
            return null;
        }
        return arr.reduce((max, current) => Objects.cmp(converter != null ? converter(current) : current, converter != null ? converter(max) : max) < 0 ? current : max);
    }

    reverse<T>(arr: T[]): T[] {
        let result: T[] = [];
        for (let i = arr.length - 1; i >= 0; i--) {
            result.push(arr[i]);
        }
        return result;
    }

    safeIt<T>(arr: T[]): T[] {
        return arr ?? [];
    }

    setContained<T>(arr: T[], item: T, contained: boolean): T[] {
        if (contained && !arr.includes(item)) {
            return [...arr, item];
        }
        else if (!contained) {
            return this.without(arr, item);
        }
        return [...arr];
    }

    shuffle<T>(arr: T[]): T[] {
        for (let i = arr.length - 1; i > 0; i--) {
            let j = Utils.getRandomInt(i + 1);
            let x = arr[i];
            arr[i] = arr[j];
            arr[j] = x;
        }
        return arr;
    }

    shuffleSeeded<T>(arr: T[], seed: string): T[] {
        let gen: () => number = seedrandom(seed);

        for (let i = arr.length - 1; i > 0; i--) {
            let j = Utils.getRandomInt(i + 1, gen);
            let x = arr[i];
            arr[i] = arr[j];
            arr[j] = x;
        }
        return arr;
    }

    sort<T>(arr: T[], sorter: (item1: T, item2: T) => number = null, ascending = true): T[] {
        return arr.sort(sorter != null ? sorter : (a, b): number => Objects.cmp(a, b) * (ascending ? 1 : -1));
    }

    sortBy<T>(arr: T[], converter: (item: T) => any, ascending = true): T[] {
        return arr.sort((a, b) => Objects.cmp(converter(a), converter(b)) * (ascending ? 1 : -1));
    }

    sortByMultiple<T>(arr: T[], converters: ((item: T) => any)[]): T[] {
        return arr.sort((a, b) => {
            for (let converter of converters) {
                let c = Objects.cmp(converter(a), converter(b));
                if (c != 0) {
                    return c;
                }
            }
            return 0;
        });
    }

    subList<T>(arr: T[], fromIndex: number, toIndex: number = null): T[] {
        return arr.slice(Math.max(0, fromIndex), toIndex == null ? arr.length : Math.min(arr.length, toIndex + 1));
    }

    sum(arr: number[]): number {
        return arr.reduce((a, b) => a + b, 0);
    }

    toMap<V, T>(arr: T[], toKey: (item: T) => string, toValue: (item: T) => V): OrdinaryObject<V> {
        let result: OrdinaryObject<V> = {};
        for (let item of arr) {
            result[toKey(item)] = toValue(item);
        }
        return result;
    }

    toMapKeys<V>(arr: V[], toKey: (item: V) => string): OrdinaryObject<V> {
        let result: OrdinaryObject<V> = {};
        for (let item of arr) {
            result[toKey(item)] = item;
        }
        return result;
    }

    toOrdinaryObject<T, U>(arr: T[], toKey: (item: T) => string, toValue: (item: T) => U): OrdinaryObject<U> {
        let result: OrdinaryObject<U> = {};
        for (let item of arr) {
            result[toKey(item)] = toValue(item);
        }
        return result;
    }

    toOrdinaryObjectWithIndex<T, U>(arr: T[], toKey: (item: T, index: number) => string, toValue: (item: T, index: number) => U): OrdinaryObject<U> {
        let result: OrdinaryObject<U> = {};
        let index = 0;
        for (let item of arr) {
            result[toKey(item, index)] = toValue(item, index);
            index++;
        }
        return result;
    }

    toNumberMap<V, T>(arr: T[], toKey: (item: T) => number, toValue: (item: T) => V): OrdinaryObjectNumber<V> {
        let result: OrdinaryObjectNumber<V> = {};
        for (let item of arr) {
            result[toKey(item)] = toValue(item);
        }
        return result;
    }

    toNumberMapKeys<V>(arr: V[], toKey: (item: V) => number): OrdinaryObjectNumber<V> {
        let result: OrdinaryObjectNumber<V> = {};
        for (let item of arr) {
            result[toKey(item)] = item;
        }
        return result;
    }

    toMultiMap<V, T>(arr: T[], toKey: (item: T) => string, toValue: (item: T) => V): OrdinaryObject<V[]> {
        let result: OrdinaryObject<V[]> = {};
        for (let item of arr) {
            Objects.getOrAdd(result, toKey(item), (): any[] => []).push(toValue(item));
        }
        return result;
    }

    toMultiMapKeys<V>(arr: V[], toKey: (item: V) => string): OrdinaryObject<V[]> {
        let result: OrdinaryObject<V[]> = {};
        for (let item of arr) {
            Objects.getOrAdd(result, toKey(item), (): any[] => []).push(item);
        }
        return result;
    }

    toPlainMap<V, T>(arr: T[], toKey: (item: T) => string, toValue: (item: T) => V): OrdinaryObject<V> {
        let result: any = {};
        for (let item of arr) {
            result[toKey(item)] = toValue(item);
        }
        return result;
    }

    without<T>(arr: T[], toRemove: T): T[] {
        return arr.filter(item => item !== toRemove);
    }

    withoutIndex<T>(arr: T[], removeAtIndex: number, removeCount = 1): T[] {
        let result = [...arr];
        result.splice(removeAtIndex, removeCount);
        return result;
    }

    withoutNull<T>(arr: T[]): T[] {
        return arr.filter(item => item != null);
    }


    withoutSubsequentDuplicates<T>(arr: T[], toKeyConverter: (item: T) => any = null): T[] {
        if (arr == null) {
            return null;
        }
        let result: T[] = [];
        let lastKey = null;
        for (let item of arr) {
            let key = toKeyConverter != null ? toKeyConverter(item) : item;
            if (result.length == 0 || key != lastKey) {
                result.push(item);
            }
            lastKey = key;
        }
        return result;
    }

}

export let Arrays = new ArraysInternal();